'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Backdrop } from '@/components/ui/backdrop';
import { useFocusTrap } from '@/hooks/use-focus-trap';
import { useIsClient } from '@/hooks/use-is-client';
import { useModalAnimation } from '@/hooks/use-modal-animation';
import { cn } from '@/lib/utils';

// Animation duration in milliseconds (must match CSS transition duration).
// Single source of truth; base-modal re-exports for backward compatibility.
export const MODAL_ANIMATION_DURATION = 150;

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
} as const;

export type ModalSize = keyof typeof sizeClasses;

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  mobileFullScreen?: boolean;
  children: React.ReactNode;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  preventCloseWhile?: boolean;
  onAfterClose?: () => void;
  className?: string;
  /** ARIA label. Provide this OR ariaLabelledBy; a dev warning fires when neither is set. */
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  modalRef?: React.RefObject<HTMLDivElement | null>;
  zIndex?: number;
  role?: 'dialog' | 'alertdialog';
  /** Suppress the default white background + 1px border chrome. Use when
   * children provide their own edge-to-edge surface (e.g. the value paywall's
   * cream wrapper). Without this, the modal's white background + border render
   * a 1px inset gap around the child, offsetting the child's rounded corners
   * from the modal's and creating visible fringing at the corners. */
  bare?: boolean;
}

/**
 * Modal shell primitive: portal, backdrop, focus trap, ESC handling, animation,
 * and safe-area padding. Owns none of the header/body/footer conventions —
 * callers provide their own chrome inside `children`.
 *
 * Focus containment is the `useFocusTrap` hook, not `inert` on the background.
 * Same call as #1948 made for `SlidePanel`: inerting would mean reaching out to
 * neutralise sibling DOM this component has no handle on (and this one portals
 * to `document.body`, so those siblings include every other portal and toast).
 * The trap is what keeps `aria-modal="true"` below honest — without it, focus
 * walks background elements a conforming screen reader has been told to ignore.
 *
 * Escape-to-close and the caller's close control are the keyboard exits that
 * keep the trap conformant with SC 2.1.2 (No Keyboard Trap). A caller that
 * removes both owes the user some other keyboard exit: `AgreementPrompt` does
 * this deliberately when `dismissible` is false, and its exit is completing the
 * form and pressing "Accept". Don't remove both without one.
 *
 * Consumers:
 * - `BaseModal` (thin composer that adds the standard header + body + footer)
 * - Editorial value paywall modal (renders its own hero + close button in children)
 */
export function ModalShell({
  isOpen,
  onClose,
  size = 'md',
  mobileFullScreen = false,
  children,
  closeOnEscape = true,
  closeOnBackdrop = true,
  preventCloseWhile = false,
  onAfterClose,
  className,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  modalRef,
  zIndex = 90,
  role = 'dialog',
  bare = false,
}: ModalShellProps) {
  const { isAnimated, open } = useModalAnimation(isOpen);
  const [shouldMount, setShouldMount] = React.useState(isOpen);
  // Mounts in the same render `isOpen` turns true and stays true through the
  // exit animation; the close timer below clears it.
  if (isOpen && !shouldMount) setShouldMount(true);

  const handleClose = React.useCallback(() => {
    if (preventCloseWhile) return;
    onClose();
  }, [preventCloseWhile, onClose]);

  // Gates the portal until after client hydration: false on the server and
  // through the hydration pass, true from then on.
  const isClient = useIsClient();

  // Whether the dialog is committed to the DOM. `shouldMount` stays true
  // through the close animation, so this outlives `isOpen` on the way out — it
  // is the render guard below, not the trap window.
  const isRendered = shouldMount && isClient;

  // `isOpen` can be true while the dialog is not in the DOM (the server render
  // and the hydration pass). Activating the trap on `isOpen` alone would run
  // its activation effect against a still-null ref: Tab containment would
  // appear to work (that handler reads the ref lazily) while initial focus
  // silently never happened — the exact vacuous-pass shape that made #1948
  // hard to see.
  const isTrapActive = isOpen && isRendered;
  const focusTrapRef = useFocusTrap<HTMLDivElement>(isTrapActive);

  // Merge the trap's ref with the optional caller-supplied `modalRef`
  // (AgreementPrompt reaches through it to focus its first checkbox).
  const setDialogRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      focusTrapRef.current = node;
      if (modalRef) modalRef.current = node;
    },
    [focusTrapRef, modalRef]
  );

  React.useEffect(() => {
    if (!isOpen) return;
    // Capture phase + preventDefault: an open modal is the top layer, so it owns
    // Escape whether or not Escape closes it. Surfaces underneath that listen on
    // document in the bubble phase (the full-screen chat panel) check
    // defaultPrevented and stand down; otherwise one Escape on a paywall over
    // the panel closed both (#5005). Two stacked ModalShells still both close
    // on one Escape, unchanged from before.
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (closeOnEscape && !preventCloseWhile) handleClose();
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, closeOnEscape, preventCloseWhile, handleClose]);

  React.useEffect(() => {
    if (isOpen) {
      open();
    } else {
      const timer = setTimeout(() => {
        setShouldMount(false);
        if (onAfterClose) onAfterClose();
      }, MODAL_ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen, open, onAfterClose]);

  const handleBackdropClick = React.useCallback(() => {
    if (closeOnBackdrop) handleClose();
  }, [closeOnBackdrop, handleClose]);

  // Dev-only a11y warning. Fires once per prop change instead of on every
  // render (React Strict Mode would double-fire it; parent state updates
  // during animation would spam it in dev).
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !ariaLabel && !ariaLabelledBy) {
      console.warn(
        'ModalShell: must provide either ariaLabel or ariaLabelledBy for accessibility.'
      );
    }
  }, [ariaLabel, ariaLabelledBy]);

  if (!isRendered) return null;

  const modalContent = (
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center md:p-8',
        mobileFullScreen ? 'p-0' : 'p-4'
      )}
      style={{ zIndex }}
    >
      <Backdrop
        isVisible={true}
        isAnimated={isAnimated}
        onClick={handleBackdropClick}
        clickable={closeOnBackdrop}
        absolute={true}
      />
      <div
        ref={setDialogRef}
        className={cn(
          'relative shadow-md w-full',
          sizeClasses[size],
          'flex flex-col',
          mobileFullScreen
            ? 'rounded-none h-full max-h-[100dvh] md:h-auto md:max-h-[90vh] md:rounded-lg'
            : 'rounded-lg max-h-[90vh]',
          'transition-all duration-150',
          isAnimated ? 'opacity-100 scale-100' : 'opacity-0 scale-95',
          className
        )}
        style={{
          ...(bare
            ? {}
            : {
                backgroundColor: 'var(--neutral-0)',
                border: '1px solid var(--border-default)',
              }),
          ...(mobileFullScreen && {
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }),
        }}
        role={role}
        aria-modal="true"
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
        {...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
